import { easeInOutCubic, samplePath, type PathSample, type SmoothPath } from '../path';
import type { Timeline } from './types';

const clamp01 = (v: number): number => Math.max(0, Math.min(1, v));

/** Auto sweep: edge to edge with ease-in-out cubic timing. */
export function sweepTimeline(durationMs: number, startX: number, endX: number): Timeline {
  return {
    durationMs,
    xAt: (t) => startX + (endX - startX) * easeInOutCubic(clamp01(t / durationMs)),
  };
}

/** Custom pattern: replay a smoothed captured drag. */
export function pathTimeline(path: SmoothPath): Timeline {
  return { durationMs: path.duration, xAt: (t) => samplePath(path, t) };
}

/**
 * Manual drag: replay raw per-frame samples with linear interpolation and
 * hold on the last one. Returns null when there is nothing usable.
 */
export function sampledTimeline(samples: readonly PathSample[]): Timeline | null {
  if (samples.length < 2) return null;
  const first = samples[0]!;
  const last = samples[samples.length - 1]!;
  const durationMs = last.t;
  if (durationMs <= 0) return null;
  let cursor = 0;
  return {
    durationMs,
    xAt(t) {
      if (t <= first.t) return first.x;
      if (t >= durationMs) return last.x;
      // queries are usually monotonic; keep a cursor but tolerate rewinds
      if (samples[cursor]!.t > t) cursor = 0;
      while (cursor < samples.length - 2 && samples[cursor + 1]!.t <= t) cursor++;
      const a = samples[cursor]!;
      const b = samples[cursor + 1]!;
      const span = b.t - a.t || 1;
      return a.x + (b.x - a.x) * clamp01((t - a.t) / span);
    },
  };
}

/** Freeze the final position for `holdMs` so the clip has a beat before it loops. */
export function withTailHold(timeline: Timeline, holdMs: number): Timeline {
  if (holdMs <= 0) return timeline;
  return {
    durationMs: timeline.durationMs + holdMs,
    xAt: (t) => timeline.xAt(Math.min(t, timeline.durationMs)),
  };
}

/**
 * Records the live divider position once per animation frame. Sampling on
 * the frame clock (instead of on pointer events) means pauses are captured
 * as pauses and the replay matches what was actually on screen.
 */
export class DividerSampler {
  private samples: PathSample[] = [];
  private rafId = 0;
  private t0 = 0;
  private getX: (() => number) | null = null;
  private maxMs = Infinity;
  private onMax: (() => void) | null = null;

  get running(): boolean {
    return this.getX !== null;
  }

  get elapsedMs(): number {
    return this.running ? performance.now() - this.t0 : 0;
  }

  start(getX: () => number, opts: { maxMs?: number; onMax?: () => void } = {}): void {
    if (this.running) return;
    this.getX = getX;
    this.maxMs = opts.maxMs ?? Infinity;
    this.onMax = opts.onMax ?? null;
    this.t0 = performance.now();
    this.samples = [{ t: 0, x: getX() }];
    this.rafId = requestAnimationFrame(this.tick);
  }

  /** Stop and return the captured samples (always ends with a final sample). */
  stop(): PathSample[] {
    if (!this.running) return this.samples;
    cancelAnimationFrame(this.rafId);
    const getX = this.getX!;
    this.getX = null;
    const t = Math.min(performance.now() - this.t0, this.maxMs);
    this.samples.push({ t, x: getX() });
    return this.samples;
  }

  private tick = (): void => {
    if (!this.getX) return;
    const t = performance.now() - this.t0;
    if (t >= this.maxMs) {
      this.onMax?.();
      return;
    }
    this.samples.push({ t, x: this.getX() });
    this.rafId = requestAnimationFrame(this.tick);
  };
}
